// src/components/GeneralRandevuFormModal.js
//
// Takvimden randevu oluşturma / düzenleme / silme modalı.
// Faz 3 notu: v2'de bu modal hiçbir yerden import edilmiyordu (ölü kod) ve
// kaydettiği dokümanda 'durum' alanı hiç yoktu — üretilen randevu takvimin
// durum filtresinin dışında kalıp görünmez oluyordu. Artık şema tek üreticiden
// (buildAppointmentData) çıkar ve durum her zaman yazılır.

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, doc, updateDoc, deleteDoc, deleteField, serverTimestamp, onSnapshot, query } from 'firebase/firestore';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Box, CircularProgress, FormControl, InputLabel, Select, MenuItem, Stack, Autocomplete, Alert, Chip } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { trFilterOptions } from '../utils/search';
import {
    buildAppointmentData,
    normalizeRandevuDurum,
    randevuEtkinlikTurleri,
    randevuHizmetTurleri,
    getRandevuStart,
    getRandevuEnd,
    VARSAYILAN_RANDEVU_DURUMU
} from './AppointmentConstants';

const SILEBILEN_ROLLER = ['Genel Müdür', 'Müdür'];

const GeneralRandevuFormModal = ({ isOpen, onClose, initialData, dateRange, userRole }) => {

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [serviceType, setServiceType] = useState('');
    const [notes, setNotes] = useState('');

    const [eventType, setEventType] = useState('Görüşme');

    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);

    const [isSaving, setIsSaving] = useState(false);
    const [hata, setHata] = useState('');
    const isEditing = Boolean(initialData);
    const silebilir = isEditing && SILEBILEN_ROLLER.includes(userRole);
    // Düzenlemede mevcut durum korunur; yeni kayıt 'Bekleniyor' olarak açılır.
    const mevcutDurum = isEditing ? normalizeRandevuDurum(initialData?.durum) : VARSAYILAN_RANDEVU_DURUMU;

    useEffect(() => {
        const q = query(collection(db, "customers"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, label: `${doc.data().adSoyad} (${doc.data().telefon})` })));
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (isOpen) {
            setHata('');
            if (isEditing && initialData) {
                setCustomerName(initialData.customerName || initialData.musteriAdSoyad || '');
                setCustomerPhone(initialData.customerPhone || '');
                setCustomerId(initialData.customerId || '');
                setServiceType(initialData.serviceType || '');
                setNotes(initialData.notes || '');
                setEventType(initialData.eventType || 'Görüşme');
                setStartTime(getRandevuStart(initialData));
                setEndTime(getRandevuEnd(initialData));

                const matchingCustomer = customers.find(c => c.id === initialData.customerId);
                setSelectedCustomer(matchingCustomer || null);

            } else if (dateRange) {
                resetForm();
                setStartTime(dateRange.start);
                setEndTime(dateRange.end);
                setEventType('Görüşme');
            } else {
                resetForm();
                setEventType('Görüşme');
            }
        }
    }, [isOpen, initialData, dateRange, isEditing, customers]);

    const resetForm = () => {
        setCustomerName('');
        setCustomerPhone('');
        setCustomerId('');
        setServiceType('');
        setNotes('');
        setEventType('Görüşme');
        setStartTime(null);
        setEndTime(null);
        setSelectedCustomer(null);
    };

    const handleCustomerChange = (event, newValue) => {
        setSelectedCustomer(newValue);
        if (newValue) {
            setCustomerName(newValue.adSoyad);
            setCustomerPhone(newValue.telefon);
            setCustomerId(newValue.id);
        } else {
            setCustomerName('');
            setCustomerPhone('');
            setCustomerId('');
        }
    };

    const handleSubmit = async () => {
        setHata('');
        if (!customerName || !startTime || !endTime || !serviceType || !eventType) {
            setHata('Müşteri Adı, Hizmet Türü, Etkinlik Türü, Başlangıç ve Bitiş zamanı zorunludur.');
            return;
        }
        if (endTime <= startTime) {
            setHata('Bitiş zamanı başlangıç zamanından sonra olmalıdır.');
            return;
        }

        setIsSaving(true);
        try {
            const dataToSave = buildAppointmentData({
                customerName,
                customerPhone,
                customerId,
                serviceType,
                eventType,
                notes,
                start: startTime,
                end: endTime,
                durum: mevcutDurum
            });

            if (isEditing) {
                // Müşteri bağı kaldırıldıysa alan tamamen silinir (boş string bırakılmaz).
                if (!customerId) dataToSave.customerId = deleteField();
                await updateDoc(doc(db, "appointments", initialData.id), dataToSave);
            } else {
                await addDoc(collection(db, "appointments"), {
                    ...dataToSave,
                    createdAt: serverTimestamp()
                });
            }
            onClose();
        } catch (error) {
            console.error("Randevu kaydedilirken hata:", error);
            setHata(error?.message || 'Randevu kaydedilirken bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!silebilir) return;
        const onay =
            `${customerName || 'Bu'} randevusu kalıcı olarak silinecek.\n\n` +
            `İptal edilen randevuyu saklamak istiyorsanız silmek yerine Randevu Detayı ekranından "İptal Et" seçeneğini kullanın.\n\n` +
            `BU İŞLEM GERİ ALINAMAZ. Devam edilsin mi?`;
        if (!window.confirm(onay)) return;

        setIsSaving(true);
        try {
            await deleteDoc(doc(db, "appointments", initialData.id));
            onClose();
        } catch (error) {
            console.error("Randevu silinirken hata:", error);
            setHata('Silme işlemi sırasında bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                {isEditing ? 'Randevuyu Düzenle' : 'Yeni Randevu Oluştur'}
                <Chip size="small" label={`Durum: ${mevcutDurum}`} />
            </DialogTitle>
            <DialogContent>
                <Stack component="form" sx={{ mt: 2 }} spacing={2}>

                    {hata && <Alert severity="error">{hata}</Alert>}

                    <Autocomplete
                        value={selectedCustomer}
                        onChange={handleCustomerChange}
                        options={customers}
                        getOptionLabel={(option) => option.label || ''}
                        filterOptions={trFilterOptions}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Kayıtlı Müşteri Ara"
                                variant="outlined"
                            />
                        )}
                    />
                    <TextField label="Müşteri Adı Soyadı" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
                    <TextField label="Müşteri Telefon" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />

                    <FormControl fullWidth><InputLabel>Hizmet Türü</InputLabel>
                        <Select value={serviceType} label="Hizmet Türü" onChange={(e) => setServiceType(e.target.value)} required>
                            {randevuHizmetTurleri.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth><InputLabel>Etkinlik Türü</InputLabel>
                        <Select value={eventType} label="Etkinlik Türü" onChange={(e) => setEventType(e.target.value)}>
                            {randevuEtkinlikTurleri.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <DateTimePicker
                        label="Başlangıç Zamanı"
                        value={startTime}
                        onChange={setStartTime}
                        ampm={false}
                        slotProps={{ textField: { fullWidth: true, required: true } }}
                    />
                    <DateTimePicker
                        label="Bitiş Zamanı"
                        value={endTime}
                        onChange={setEndTime}
                        ampm={false}
                        slotProps={{ textField: { fullWidth: true, required: true } }}
                    />

                    <TextField label="Randevu Notları" value={notes} onChange={(e) => setNotes(e.target.value)} multiline rows={3} />

                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: '16px 24px', justifyContent: 'space-between' }}>
                {/* Silme yalnızca Genel Müdür / Müdür rolünde */}
                {silebilir ? (
                    <Button onClick={handleDelete} color="error" disabled={isSaving}>
                        Sil
                    </Button>
                ) : <div />}

                <Box>
                    <Button onClick={onClose} disabled={isSaving} sx={{ mr: 1 }}>İptal</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
                        {isSaving ? <CircularProgress size={24} /> : (isEditing ? 'Güncelle' : 'Kaydet')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default GeneralRandevuFormModal;
